import { useMemo, useState } from 'react';

import { ArrowRightIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router';

import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { getLastVisitedCategory } from '@/modules/aiAgents/helper';
import { useComparisonMode } from '@/modules/analytics/hooks/useComparisonMode';
import { formatScore } from '@/modules/analytics/utils/formatScore';
import { selectAccountSlug } from '@/redux/session/selectors';

import type { AiAgentMetricKey } from './aiAgentsInsightsConfig';
import { AnalyticsEmptyState } from './AnalyticsEmptyState';
import { CompareTrendCell } from './CompareTrendCell';
import { useAiAgents } from './hooks/useAiAgents';
import { useSelectedAiAgentMetric } from './hooks/useSelectedAiAgentMetric';

type SortDirection = 'asc' | 'desc';

interface MetricColumnConfig {
  /** Header label as shown in the table — short, column-style. */
  header: string;
  /** Formats the numeric value for the cell. */
  format: (value: number) => string;
  /** True when a *decrease* is favorable (e.g. response time). */
  invertTrend?: boolean;
  /** Default sort direction so the "best" agents land at the top. */
  defaultDirection: SortDirection;
}

const METRIC_COLUMN_CONFIG: Record<AiAgentMetricKey, MetricColumnConfig> = {
  csat: { header: 'AI CSAT (1-5)', format: formatScore, defaultDirection: 'desc' },
  containment: { header: 'Containment', format: (v) => `${v}%`, defaultDirection: 'desc' },
  responseTime: {
    header: 'Response time',
    format: (v) => `${v.toFixed(1)}s`,
    invertTrend: true,
    defaultDirection: 'asc',
  },
  totalConversations: {
    header: 'Conversations',
    format: (v) => v.toLocaleString(),
    defaultDirection: 'desc',
  },
};

/**
 * AI Agents per-agent metrics table.
 *
 * The right-hand column mirrors whichever KPI tile is currently selected
 * (CSAT, containment, response time, or total conversations) and is the only
 * sortable column — the agent column is intentionally read-only. Page-scoped,
 * not a reusable/shared component.
 */
export function AiAgentsTable() {
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { data: agents, isLoading } = useAiAgents();
  const { isComparing } = useComparisonMode();
  const { selectedMetric } = useSelectedAiAgentMetric();

  const column = METRIC_COLUMN_CONFIG[selectedMetric];

  // Sort direction starts at the metric's "best-first" default. Switching
  // tiles snaps back to that metric's default; clicking the metric header
  // toggles asc/desc within the same metric.
  const [sortDirection, setSortDirection] = useState<SortDirection>(column.defaultDirection);
  const [lastDefaultedMetric, setLastDefaultedMetric] = useState<AiAgentMetricKey>(selectedMetric);

  if (lastDefaultedMetric !== selectedMetric) {
    setLastDefaultedMetric(selectedMetric);
    setSortDirection(column.defaultDirection);
  }

  const rows = useMemo(() => {
    // Hide rows whose selected-metric value is a "no data" sentinel. The
    // data hook coerces null → 0 for every metric, so 0 reads as missing
    // for csat (1-5 scale), response time, and total conversations. The
    // exception is containment: 0% is a *real* value (100%-escalation
    // agent) that we want to keep — distinguish it from "no data" by
    // checking total conversations instead. The export pulls the
    // unfiltered list from `useAiAgents` regardless.
    const filtered = agents.filter((a) =>
      selectedMetric === 'containment'
        ? a.current.totalConversations > 0
        : a.current[selectedMetric] > 0
    );
    const next = [...filtered].sort(
      (a, b) => a.current[selectedMetric] - b.current[selectedMetric]
    );
    return sortDirection === 'asc' ? next : next.reverse();
  }, [agents, sortDirection, selectedMetric]);

  const toggleSort = () => {
    setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
  };

  // getLastVisitedCategory reads localStorage — look it up once per data change, not per row render
  const categoryById = useMemo(
    () => new Map(agents.map((a) => [a.id, getLastVisitedCategory(a.id)])),
    [agents]
  );

  // Empty state covers two cases: a date range with no data at all, or a
  // metric whose zero rows we filtered out (CSAT / response time) leaving
  // nothing to show. Skeletons still win during the initial load so the
  // user doesn't see "no data" before the request resolves.
  const isEmpty = !isLoading && rows.length === 0;

  return isEmpty ? (
    <div className="flex items-center justify-center py-12">
      <AnalyticsEmptyState
        title={t('analytics.no_data_available')}
        description={t('analytics.no_data_for_period')}
      />
    </div>
  ) : (
    <Table className="max-h-96 overscroll-x-none overscroll-y-auto">
      <TableHeader>
        <TableRow>
          <TableHead className="px-4">
            <span className="text-sm leading-6 font-medium text-foreground-muted">AI agent</span>
          </TableHead>
          <TableHead className="px-4 text-right">
            <button
              type="button"
              onClick={toggleSort}
              className="inline-flex items-center justify-end gap-1 text-sm leading-6 font-medium text-foreground-muted hover:text-foreground"
            >
              {column.header}
              <ChevronsUpDownIcon className="size-3.5 text-foreground" />
            </button>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading && rows.length === 0
          ? Array.from({ length: 5 }, (_, i) => (
              <TableRow key={`skeleton-${i}`}>
                <TableCell className="px-4">
                  <Skeleton className="h-5 w-48" />
                </TableCell>
                <TableCell className="px-4 text-right">
                  <Skeleton className="ml-auto h-5 w-12" />
                </TableCell>
              </TableRow>
            ))
          : rows.map((row) => {
              const current = row.current[selectedMetric];
              const previous = row.previous[selectedMetric];
              const href = `/${slug}/ai-agents/${row.id}/${categoryById.get(row.id)}`;
              return (
                <TableRow key={row.id} className="group cursor-pointer">
                  <Link to={href} className="contents">
                    <TableCell className="px-4">
                      <div className="flex items-center gap-2">
                        <span className="flex size-5 shrink-0 items-center justify-center text-foreground-muted [&_svg]:size-4">
                          {row.icon}
                        </span>
                        <span className="text-sm leading-5 font-normal text-foreground">
                          {row.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 text-right text-sm leading-5 font-normal text-foreground tabular-nums">
                      <div className="inline-flex items-center justify-end gap-3">
                        <span
                          aria-hidden
                          className="inline-flex items-center gap-1 text-sm leading-5 font-medium text-informational opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
                        >
                          {t('analytics.view_ai_agent')}
                          <ArrowRightIcon className="size-3.5" />
                        </span>
                        {isComparing && (
                          <CompareTrendCell
                            current={current}
                            previous={previous}
                            formatValue={column.format}
                            invertColors={column.invertTrend}
                          />
                        )}
                        <span>{column.format(current)}</span>
                      </div>
                    </TableCell>
                  </Link>
                </TableRow>
              );
            })}
      </TableBody>
    </Table>
  );
}
